import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getExpandedRowModel } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { parseFormNumber } from '@/helpers/finance';
import { useDataTable } from '@/hooks/use-data-table';
import { useModal } from '@/hooks/use-modal';
import { DEFAULT_PAGE_SIZE } from '@/lib/constants/pagination';
import { TableIds } from '@/lib/constants/table-ids';
import { Currency } from '@/modules/common';
import { DateRangePresetCategory } from '@/modules/common/components/molecules/date/date-range-picker-v2';
import { useFeatureFlags } from '@/modules/common/hooks/use-feature-flags';
import { TeamMemberStatusDTO } from '@/modules/common/types/team-member-status-dto';
import {
  GenerateEntityTemplate,
  hasBlockingQuickBooksWarnings,
  useGenerateEntityPage,
  useGenerateRefresh,
  useGenerateSelectionState,
} from '@/modules/finance/common';
import { AddCustomEntityButton } from '@/modules/finance/common/components/add-custom-entity-button';
import {
  getDefaultPaymentDetails,
  usePaymentDetailsShort,
} from '@/modules/settings/hooks/payment-details/use-payment-details-short';
import { getShortTeamMembers } from '@/modules/team';
import { NON_ARCHIVED_TEAM_MEMBER_STATUSES } from '@/modules/team/types/dto/find-short-team-members.request';
import { AuthenticatedRoutes } from '@/router/routes';

import { GenerateInvoicesFooter, GenerateInvoicesModal } from '../components';
import {
  getInvoiceProjectRowId,
  getInvoiceRowId,
  getInvoiceSubRowId,
} from '../components/generate-invoices-table/invoice-row.helpers';
import { PaymentDetailsSelector } from '../components/payment-details/payment-details-cell';
import {
  GENERATE_INVOICES_TABLE_FILTERS_STORAGE_KEY,
  getGenerateInvoicesFiltersConfig,
  getGenerateInvoicesTableColumns,
  getInvoiceFieldName,
  getInvoiceProjectPath,
  INVOICES_FILTER_KEY_BILLING_MODEL,
  INVOICES_FILTER_KEY_BILLING_PERIOD,
  INVOICES_FILTER_KEY_CLIENT,
  INVOICES_FILTER_KEY_PROJECT,
  INVOICES_FILTER_KEY_SEARCH,
  INVOICES_TABLE_FILTER_KEYS,
} from '../constants';
import {
  mapGenerateInvoiceDataFilters,
  useGenerateInvoiceData,
  useGenerateInvoicesSmartRefresh,
  useInvoiceCustomProjects,
} from '../hooks';
import {
  GenerateInvoiceAddedMemberListItem,
  GenerateInvoiceAddOverheadButtonRow,
  GenerateInvoiceListItem,
  GenerateInvoiceProjectOverheadListItem,
  GenerateInvoiceProjectTeamMemberListItem,
  GenerateInvoiceSubRow,
  GenerateInvoiceSubRowType,
} from '../types';
import {
  GenerateInvoicesTableFormValues,
  generateInvoicesValidationSchema,
} from '../validations/generate-invoices-validation';

// Year/Quarter presets are hidden here because this screen loads the whole range
// eagerly and the backend currently fails on year/quarter-sized datasets. Restore
// them once this screen moves to the lazyload approach.
// TODO(lazyload): re-enable Year/Quarter presets when generate is paginated.
const CUSTOM_PROJECT_PICKER_QUERY_KEY = ['generate-invoices-custom-project-options'];

const GENERATE_HIDDEN_PRESET_CATEGORIES = [
  DateRangePresetCategory.Year,
  DateRangePresetCategory.Quarter,
];

export const GenerateInvoicesPage = () => {
  const { t } = useTranslation(['authenticated.invoices', 'common'], { keyPrefix: 'new' });
  const { t: tCommon } = useTranslation('common');

  const navigate = useNavigate();
  const navigateToInvoices = () => navigate(AuthenticatedRoutes.Invoices);

  const {
    open: isConfirmModalOpen,
    onClose: closeConfirmModal,
    onOpen: openConfirmModal,
  } = useModal();

  const filtersConfig = useMemo(() => getGenerateInvoicesFiltersConfig(), []);

  // Page-level state for user-added overhead and member rows. Kept outside the
  // form because RHF can't track row creation/deletion on its own.
  const [projectOverheads, setProjectOverheads] = useState<
    Record<string, GenerateInvoiceProjectOverheadListItem[]>
  >({});
  const pendingOverheadSelections = useRef<string[]>([]);

  const [projectAddedMembers, setProjectAddedMembers] = useState<
    Record<string, GenerateInvoiceAddedMemberListItem[]>
  >({});
  const pendingAddedMemberSelections = useRef<string[]>([]);

  const hasOverheads = Object.keys(projectOverheads).length > 0;
  const hasAddedMembers = Object.keys(projectAddedMembers).length > 0;

  // Per-invoice payment-details selection (OPS-1610). Only user overrides are stored;
  // untouched rows follow the organization default derived from the short list below,
  // so default preselection never marks the page dirty and selections survive smart
  // refresh / filter changes (keyed by project id).
  const { showPaymentDetails } = useFeatureFlags();
  const [paymentDetailsOverrides, setPaymentDetailsOverrides] = useState<
    Record<string, number | null>
  >({});

  const { data: paymentDetailsOptions = [] } = usePaymentDetailsShort({
    enabled: showPaymentDetails,
  });
  const defaultPaymentDetailsId = getDefaultPaymentDetails(paymentDetailsOptions)?.id ?? null;

  const hasPaymentDetailsChanges =
    showPaymentDetails &&
    Object.values(paymentDetailsOverrides).some(id => id !== defaultPaymentDetailsId);

  const invoiceCustomProjects = useInvoiceCustomProjects();

  const page = useGenerateEntityPage<GenerateInvoicesTableFormValues>({
    filterKeys: {
      billingPeriod: INVOICES_FILTER_KEY_BILLING_PERIOD,
      search: INVOICES_FILTER_KEY_SEARCH,
      arrayKeys: [
        INVOICES_FILTER_KEY_CLIENT,
        INVOICES_FILTER_KEY_PROJECT,
        INVOICES_FILTER_KEY_BILLING_MODEL,
      ],
    },
    filtersStorageKey: GENERATE_INVOICES_TABLE_FILTERS_STORAGE_KEY,
    filterKeysList: INVOICES_TABLE_FILTER_KEYS,
    filtersConfig,
    formSchema: generateInvoicesValidationSchema,
    unsavedChangesEntityLabel: t('common.invoices'),
    additionalIsDirty:
      hasOverheads ||
      hasAddedMembers ||
      hasPaymentDetailsChanges ||
      invoiceCustomProjects.hasCustomProjects,
    onDiscardAdditional: () => {
      setProjectOverheads({});
      setProjectAddedMembers({});
      setPaymentDetailsOverrides({});
      invoiceCustomProjects.reset();
    },
  });

  const addOverhead = useCallback((projectId: string, currencyCode: string) => {
    const newItem: GenerateInvoiceProjectOverheadListItem = {
      type: GenerateInvoiceSubRowType.OVERHEAD,
      id: crypto.randomUUID(),
      description: '',
      effectiveRate: 0,
      currency: { code: currencyCode, displayName: '' } as Currency,
    };
    pendingOverheadSelections.current.push(
      getInvoiceSubRowId(getInvoiceProjectRowId(projectId), newItem)
    );
    setProjectOverheads(prev => ({
      ...prev,
      [projectId]: [...(prev[projectId] ?? []), newItem],
    }));
  }, []);

  // Fields are keyed by the row's own UUID, so removing a custom row just unregisters that
  // row's field group and drops it from state — no index re-shuffling of siblings needed.
  const removeOverhead = useCallback(
    (projectId: string, overheadId: string) => {
      page.form.unregister(`${getInvoiceProjectPath(Number(projectId))}.overhead.o${overheadId}`);
      setProjectOverheads(prev => {
        const updated = (prev[projectId] ?? []).filter(o => o.id !== overheadId);
        const result = { ...prev, [projectId]: updated };
        if (!updated.length) delete result[projectId];
        return result;
      });
    },
    [page.form]
  );

  const addMember = useCallback((projectId: string, currencyCode: string) => {
    const newItem: GenerateInvoiceAddedMemberListItem = {
      type: GenerateInvoiceSubRowType.ADDED_MEMBER,
      id: crypto.randomUUID(),
      memberId: null,
      memberName: '',
      avatarUrl: '',
      hours: 0,
      effectiveRate: 0,
      currency: { code: currencyCode, displayName: '' } as Currency,
      total: 0,
    };
    pendingAddedMemberSelections.current.push(
      getInvoiceSubRowId(getInvoiceProjectRowId(projectId), newItem)
    );
    setProjectAddedMembers(prev => ({
      ...prev,
      [projectId]: [...(prev[projectId] ?? []), newItem],
    }));
  }, []);

  const removeAddedMember = useCallback(
    (projectId: string, addedMemberId: string) => {
      page.form.unregister(
        `${getInvoiceProjectPath(Number(projectId))}.addedMember.a${addedMemberId}`
      );
      setProjectAddedMembers(prev => {
        const updated = (prev[projectId] ?? []).filter(m => m.id !== addedMemberId);
        const result = { ...prev, [projectId]: updated };
        if (!updated.length) delete result[projectId];
        return result;
      });
    },
    [page.form]
  );

  const overheadActions = useMemo(
    () => ({ addOverhead, removeOverhead }),
    [addOverhead, removeOverhead]
  );
  const addedMemberActions = useMemo(
    () => ({ addMember, removeAddedMember }),
    [addMember, removeAddedMember]
  );

  const {
    data,
    isLoading,
    error,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    dataUpdatedAt,
    fetchNextPage,
  } = useGenerateInvoiceData({ filters: page.appliedFilters });

  const apiFilters = useMemo(
    () => mapGenerateInvoiceDataFilters(page.appliedFilters),
    [page.appliedFilters]
  );

  const { hasStaleData, isRefreshing, applySmartRefresh } = useGenerateInvoicesSmartRefresh({
    filters: page.appliedFilters,
    apiFilters,
    enabled: page.isBillingPeriodSelected,
    items: data?.items,
  });

  // Paginated server-side loaders for the "Add custom member" picker. Each
  // picker instance shares the same TanStack-Query cache (same queryKey), so
  // opening multiple custom-member rows on the page doesn't multiply requests.
  //
  // PLACEHOLDER members are excluded — you can't invoice a placeholder, and
  // they shouldn't appear as choices when adding a custom invoice line.
  const ADDED_MEMBER_STATUSES = useMemo(
    () => NON_ARCHIVED_TEAM_MEMBER_STATUSES.filter(s => s !== TeamMemberStatusDTO.PLACEHOLDER),
    []
  );
  const loadAddedMemberOptions = useCallback(
    (cursor?: number | null) =>
      getShortTeamMembers({
        filter: { statuses: ADDED_MEMBER_STATUSES },
        pagination: { cursor, pageSize: DEFAULT_PAGE_SIZE },
      }),
    [ADDED_MEMBER_STATUSES]
  );
  const searchAddedMemberOptions = useCallback(
    (searchTerm: string, cursor?: number | null) =>
      getShortTeamMembers({
        filter: { searchString: searchTerm, statuses: ADDED_MEMBER_STATUSES },
        pagination: { cursor, pageSize: DEFAULT_PAGE_SIZE },
      }),
    [ADDED_MEMBER_STATUSES]
  );
  const addedMemberSelector = useMemo(
    () => ({ loadItems: loadAddedMemberOptions, searchItems: searchAddedMemberOptions }),
    [loadAddedMemberOptions, searchAddedMemberOptions]
  );

  // Rebuilds when a selection changes (overrides map in deps) — same cost class as
  // the overhead/added-member interactions; TanStack keeps table state across it.
  const paymentDetailsSelector = useMemo<PaymentDetailsSelector | null>(
    () =>
      showPaymentDetails
        ? {
            options: paymentDetailsOptions,
            defaultId: defaultPaymentDetailsId,
            getSelected: projectId =>
              paymentDetailsOverrides[projectId] !== undefined
                ? paymentDetailsOverrides[projectId]
                : defaultPaymentDetailsId,
            hasOverride: projectId => paymentDetailsOverrides[projectId] !== undefined,
            onSelect: (projectId, paymentDetailsId) =>
              setPaymentDetailsOverrides(prev => ({ ...prev, [projectId]: paymentDetailsId })),
          }
        : null,
    [showPaymentDetails, paymentDetailsOptions, defaultPaymentDetailsId, paymentDetailsOverrides]
  );

  const columns = useMemo(
    () =>
      getGenerateInvoicesTableColumns(
        page.form,
        overheadActions,
        addedMemberActions,
        addedMemberSelector,
        paymentDetailsSelector
      ),
    // Form object is stable from useForm, only recreate columns if handlers change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [overheadActions, addedMemberActions, addedMemberSelector, paymentDetailsSelector]
  );

  // Compose the server projects (members + scheduled overheads) with user-added
  // overheads/added-members and the ADD_OVERHEAD button row (only for projects
  // connected to QuickBooks).
  // Combine server-loaded and custom project IDs for the exclude set
  const allProjectIds = useMemo(() => {
    const ids = new Set(invoiceCustomProjects.customProjectIds);
    if (data?.items) {
      for (const item of data.items) {
        ids.add(item.project.id);
      }
    }
    return ids;
  }, [data?.items, invoiceCustomProjects.customProjectIds]);

  const tableData = useMemo(() => {
    // Server projects and hand-added ones go through ONE composition pass, so the custom lines a
    // user adds to a custom project materialize exactly as they do on a server project.
    // A custom project that the server has since returned is dropped: both rows would claim the
    // same TanStack row id, and the custom lines follow the project id onto the server row.
    const serverProjectIds = new Set((data?.items ?? []).map(project => project.id));
    const allProjects = [
      ...(data?.items ?? []),
      ...invoiceCustomProjects.customProjects.filter(project => !serverProjectIds.has(project.id)),
    ];

    return allProjects.map(project => {
      const addedMembers = projectAddedMembers[project.id] ?? [];
      const overheads = projectOverheads[project.id] ?? [];
      // project.projectSubRows already contains server-scheduled overhead rows
      // (mapped from the Overheads page); user-added ad-hoc lines follow them.
      const subRows: GenerateInvoiceSubRow[] = [
        ...project.projectSubRows,
        ...addedMembers,
        ...overheads,
      ];
      if (!hasBlockingQuickBooksWarnings(project.project.warnings)) {
        const addOverheadRow: GenerateInvoiceAddOverheadButtonRow = {
          type: GenerateInvoiceSubRowType.ADD_OVERHEAD,
          projectId: String(project.id),
          currencyCode: project.currency.code,
        };
        subRows.push(addOverheadRow);
      }
      return {
        ...project,
        projectAddedMembers: addedMembers,
        projectOverheads: [...project.projectOverheads, ...overheads],
        projectSubRows: subRows,
      };
    });
  }, [data?.items, projectAddedMembers, projectOverheads, invoiceCustomProjects.customProjects]);

  const { table, saveColumnSizing } = useDataTable<GenerateInvoiceListItem>({
    data: tableData,
    columns,
    getRowId: getInvoiceRowId,
    enableRowSelection: row => {
      const subRow = row.original as unknown as GenerateInvoiceSubRow;
      return subRow.type !== GenerateInvoiceSubRowType.ADD_OVERHEAD;
    },
    getExpandedRowModel: getExpandedRowModel(),
    // @ts-expect-error projectSubRows model and row.original aren't same
    getSubRows: row => row.projectSubRows,
    initialState: { columnPinning: { left: ['select', 'project'] } },
    tableId: TableIds.GENERATE_INVOICES,
  });

  // Auto-select newly added overhead / member / custom project rows once they materialize.
  useEffect(() => {
    const overheadIds = pendingOverheadSelections.current;
    const memberIds = pendingAddedMemberSelections.current;
    if (overheadIds.length === 0 && memberIds.length === 0) {
      invoiceCustomProjects.flushPendingRows(table);
      return;
    }
    pendingOverheadSelections.current = [];
    pendingAddedMemberSelections.current = [];
    table.setRowSelection(prev => {
      const next = { ...prev };
      [...overheadIds, ...memberIds].forEach(id => {
        next[id] = true;
      });
      return next;
    });
    invoiceCustomProjects.flushPendingRows(table);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData, table]);

  const { refreshButtonProps } = useGenerateRefresh({
    form: page.form,
    table,
    hasStaleData,
    isRefreshing,
    isFetching,
    dataUpdatedAt,
    applySmartRefresh,
  });

  const { selectedRowObjects, selectedRowsCount, hasTableRows, pulseContainerRef, clearSelection } =
    useGenerateSelectionState({
      table,
      itemsCount: data?.items?.length ?? 0,
    });

  const getInvoiceProjects = useCallback(() => {
    const { getValues } = page.form;
    // Read a leaf's edited value by its id-keyed field path (no index bookkeeping).
    const readField = (subRow: (typeof selectedRowObjects)[number], name: string) =>
      getValues(getInvoiceFieldName(subRow, name) as never) as unknown;

    const selectedProjectRowsMap = new Map<
      number,
      ReturnType<typeof table.getCoreRowModel>['rows'][0]
    >();

    selectedRowObjects.forEach(rowObject => {
      const parentRow = rowObject.getParentRow();
      if (!parentRow) return;
      const projectId = parentRow.original.project.id;
      if (!selectedProjectRowsMap.has(projectId)) {
        selectedProjectRowsMap.set(projectId, parentRow);
      }
    });

    return Array.from(selectedProjectRowsMap.values()).map(projectRow => {
      const project = projectRow.original;

      const membersWithFormValues: GenerateInvoiceProjectTeamMemberListItem[] = [];
      const addedMembersWithFormValues: GenerateInvoiceAddedMemberListItem[] = [];
      const overheadsWithFormValues: GenerateInvoiceProjectOverheadListItem[] = [];

      for (const subRow of projectRow.subRows) {
        const subRowOriginal = subRow.original as unknown as GenerateInvoiceSubRow;
        if (subRowOriginal.type === GenerateInvoiceSubRowType.ADD_OVERHEAD) continue;
        if (!selectedRowObjects.some(rowObject => rowObject === subRow)) continue;

        if (subRowOriginal.type === GenerateInvoiceSubRowType.OVERHEAD) {
          const overhead = subRowOriginal;
          const description = String(readField(subRow, 'description') ?? overhead.description);
          const effectiveRate = parseFormNumber(
            (readField(subRow, 'effectiveRate') as number | undefined) ?? overhead.effectiveRate
          );
          if (description.trim() !== '') {
            overheadsWithFormValues.push({ ...overhead, description, effectiveRate });
          }
          continue;
        }

        if (subRowOriginal.type === GenerateInvoiceSubRowType.ADDED_MEMBER) {
          const addedMember = subRowOriginal;
          const rawMemberId = readField(subRow, 'memberId') as number | null | undefined;
          const memberId = rawMemberId ? parseInt(String(rawMemberId), 10) : null;
          const hours = parseFormNumber((readField(subRow, 'hours') as number | undefined) ?? 0);
          const effectiveRate = parseFormNumber(
            (readField(subRow, 'effectiveRate') as number | undefined) ?? 0
          );
          if (memberId) {
            addedMembersWithFormValues.push({
              ...addedMember,
              memberId,
              hours,
              effectiveRate,
              total: hours * effectiveRate,
            });
          }
          continue;
        }

        const member = subRowOriginal;
        const hours = (readField(subRow, 'hours') as number | undefined) ?? member.hours;
        const effectiveRate =
          (readField(subRow, 'effectiveRate') as number | undefined) ?? member.effectiveRate;
        const invoicedBy =
          (readField(subRow, 'invoicedBy') as typeof member.invoiceBy | undefined) ??
          member.invoiceBy;

        membersWithFormValues.push({
          ...member,
          hours,
          effectiveRate,
          invoiceBy: invoicedBy,
          total: hours * effectiveRate,
        });
      }

      const isQuickBooksConnected = !hasBlockingQuickBooksWarnings(project.project.warnings);

      return {
        ...project,
        projectMembers: membersWithFormValues,
        projectAddedMembers: addedMembersWithFormValues,
        projectOverheads: overheadsWithFormValues,
        projectSubRows: [
          ...membersWithFormValues,
          ...addedMembersWithFormValues,
          ...overheadsWithFormValues,
        ],
        // undefined (flag off / not QB-connected) keeps the key out of the request payload
        paymentDetailsId:
          paymentDetailsSelector && isQuickBooksConnected
            ? paymentDetailsSelector.getSelected(project.id)
            : undefined,
      };
    });
  }, [selectedRowObjects, page.form, table, paymentDetailsSelector]);

  const modals = useMemo(
    () => (
      <GenerateInvoicesModal
        isOpen={isConfirmModalOpen}
        onClose={closeConfirmModal}
        billingPeriodLabel={page.billingPeriodLabel}
        billingPeriod={page.billingPeriodValue}
        invoiceProjects={getInvoiceProjects()}
        onSuccess={page.handleGenerateSuccess}
      />
    ),
    [
      isConfirmModalOpen,
      closeConfirmModal,
      page.billingPeriodLabel,
      page.billingPeriodValue,
      page.handleGenerateSuccess,
      getInvoiceProjects,
    ]
  );

  return (
    <GenerateEntityTemplate<GenerateInvoiceListItem, GenerateInvoicesTableFormValues>
      table={table}
      form={page.form}
      error={error}
      showDatePickerOnly={!page.isBillingPeriodSelected}
      datePickerPrompt={t('datePickerPrompt')}
      dates={page.dates}
      selectedPreset={page.selectedPreset}
      onDateChange={page.handleDateChange}
      onPresetSelect={page.handlePresetSelect}
      onDateReset={page.handleDateReset}
      dateRangeLabel={t('filters.label')}
      dateRangeInfoTooltip={t('filters.dateRangeInfo')}
      hiddenPresetCategories={GENERATE_HIDDEN_PRESET_CATEGORIES}
      filtersConfig={filtersConfig}
      filterState={page.filterState}
      refreshButtonProps={refreshButtonProps}
      hasMore={hasNextPage}
      isLoading={isLoading || isFetchingNextPage}
      onLoadMore={fetchNextPage}
      displayTitle={t('title')}
      onBack={navigateToInvoices}
      entityCounter={t(`entityCounter.${page.hasSearch ? 'withSearch' : 'common'}`, {
        count: data?.pagination.total ?? 0,
      })}
      totalCount={data?.pagination.total ?? 0}
      isAnyFilterApplied={page.isAnyFilterApplied}
      selectedItemsCounter={t('selectedItemsCounter', { count: selectedRowsCount })}
      selectedItemsCount={selectedRowsCount}
      hasTableRows={hasTableRows}
      pulseContainerRef={pulseContainerRef}
      filtersActionSlot={
        <AddCustomEntityButton
          buttonLabel={tCommon('generateEntities.addCustomProject.button')}
          buttonTooltip={tCommon('generateEntities.addCustomProject.tooltip')}
          searchPlaceholder={tCommon('generateEntities.addCustomProject.searchPlaceholder')}
          emptyMessage={tCommon('generateEntities.addCustomProject.emptyMessage')}
          allExcludedMessage={tCommon('generateEntities.addCustomProject.allAddedMessage')}
          excludeIds={allProjectIds}
          queryKey={CUSTOM_PROJECT_PICKER_QUERY_KEY}
          fetchItems={invoiceCustomProjects.fetchProjects}
          onSelect={invoiceCustomProjects.addCustomProject}
          renderItem={item => (
            <span className="flex items-center gap-2">
              <span className="font-medium">{item.name}</span>
              {item.clientName && (
                <span className="text-muted-foreground">— {item.clientName}</span>
              )}
            </span>
          )}
          getItemLabel={item => item.name}
        />
      }
      selectionHint={tCommon('generateEntities.selectionHintInvoices')}
      onClearClick={clearSelection}
      onGenerateClick={openConfirmModal}
      modals={modals}
      submitButtonLabel={t('buttons.createInvoices')}
      footerSlot={
        <GenerateInvoicesFooter
          form={page.form}
          table={table}
          onClearClick={clearSelection}
          onGenerateClick={openConfirmModal}
          selectedItemsCounter={t('selectedItemsCounter', { count: selectedRowsCount })}
          selectedItemsCount={selectedRowsCount}
          submitButtonLabel={t('buttons.createInvoices')}
        />
      }
      unsavedChangesGuard={page.unsavedChangesGuardProps}
      tableId={TableIds.GENERATE_INVOICES}
      onResizeEnd={saveColumnSizing}
    />
  );
};
